import { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { FormProvider, useFieldArray } from 'react-hook-form';
import { Box } from '@mui/material';
import { useQuestionnaireModalState } from '../hooks/use-questionnaire-modal-state.hook';
import { AttendeeRegistrationFieldTypeEnum } from '../../../constants';
import { AttendeeRegistrationField } from './attendee-registration-field.component';
import { CoreLabeledCheckbox, CoreLinkButton, Option } from '../../_core';
import { AttendeeRegistrationFieldFormDto, AttendeeRegistrationFormDto, TiersTypeEnum } from '../../../models/dto';
import { PlusIcon } from '../../../icons';
import { RightBlackModal } from '../../right-modal';
import { Showable } from '../../utils';
import { scrollToFirstErrorElement } from '../../../utils';

const defaultAttendeeRegistrationField: AttendeeRegistrationFieldFormDto = {
	question: 'Add a question',
	type: AttendeeRegistrationFieldTypeEnum.TextInput,
	required: true,
	isCommonForAllTickets: false,
	tiersType: TiersTypeEnum.All,
};

export enum QuestionnaireModalTypeEnum {
	Event = 'event',
	Membership = 'membership',
}

export interface QuestionnaireModalProps<TValues = AttendeeRegistrationFormDto> {
	open: boolean;
	type: QuestionnaireModalTypeEnum;
	questionnaireImages: Record<AttendeeRegistrationFieldTypeEnum, string>;
	initialData?: TValues;
	tiersOptions?: Option[];
	isPaidEvent?: boolean;
	shouldValidateInitialData?: boolean;
	onClose?(isDirty?: boolean): void;
	onSubmit?(data: TValues): void;
}

export const QuestionnaireModal: FC<QuestionnaireModalProps> = props => {
	const {
		open,
		type,
		initialData,
		isPaidEvent,
		tiersOptions,
		questionnaireImages,
		shouldValidateInitialData,
		onSubmit,
		onClose,
	} = props;

	const formRef = useRef<HTMLDivElement>(null);

	const handleSave = useCallback((data: AttendeeRegistrationFormDto): void => {
		onSubmit?.(data);
	}, [onSubmit]);

	const onInvalid = useCallback((): void => {
		scrollToFirstErrorElement(formRef.current);
	}, []);

	const defaultValues = useMemo(() => {
		if (type === QuestionnaireModalTypeEnum.Event) {
			return {
				isReservedFieldsRequires: true,
				fullNameReservedField: { label: 'Full Name', required: true, enabled: true },
				emailReservedField: { label: 'Email', required: true, enabled: true },
				phoneReservedField: { label: 'Phone Number', required: true, enabled: true },
				showSectionsOnceForMultipleTickets: false,
				...(initialData ?? {}),
				fields: initialData?.fields?.length ? initialData.fields : [defaultAttendeeRegistrationField],
			};
		}

		return {
			isReservedFieldsRequires: false,
			...(initialData ?? {}),
			fields: initialData?.fields?.length ? initialData.fields : [defaultAttendeeRegistrationField],
		}
	}, [initialData, type]);

	const { form: questionnaireForm } = useQuestionnaireModalState({
		defaultValues: structuredClone(defaultValues),
		mode: 'onChange',
	});

	// console.debug('intakeForm', intakeForm);
	// console.debug('errors', errors);

	const { fields, append, remove, move } = useFieldArray({
		name: `fields`,
		control: questionnaireForm.control,
		keyName: 'key',
	});

	const onceForMultipleTickets = questionnaireForm.watch('showSectionsOnceForMultipleTickets');

	const addField = useCallback((): void => {
		append({
			question: '',
			required: false,
			type: AttendeeRegistrationFieldTypeEnum.TextInput,
			isCommonForAllTickets: false,
			tiersType: TiersTypeEnum.All,
		});
	}, [append]);

	const handleFormSubmit = useCallback((e: React.FormEvent): void => {
		e.preventDefault();

		const formErrors = questionnaireForm.formState.errors;
		const hasLocalDuplicateErrors = Array.isArray(formErrors?.fields) && formErrors.fields.some(field => field?.question?.type === 'duplicate');

		if (hasLocalDuplicateErrors) {
			return;
		}

		// If no local errors, proceed with normal RHF submission
		questionnaireForm.handleSubmit(handleSave, onInvalid)();
	}, [questionnaireForm, handleSave, onInvalid]);

	const handleClose = useCallback(() => {
		onClose?.(questionnaireForm.formState.isDirty);
	}, [onClose, questionnaireForm]);

	const validateForm = useCallback(async (): Promise<void> => {
		await questionnaireForm.trigger();

		scrollToFirstErrorElement(formRef.current);
	}, [questionnaireForm]);

	useEffect(() => {
		if (shouldValidateInitialData) {
			validateForm();
		}
	}, [shouldValidateInitialData, validateForm]);

	return (
		<FormProvider {...questionnaireForm}>
			<RightBlackModal
				open={open}
				onClose={handleClose}
				headerTitle='Questionnaire'
				headerDescription='Name, email and phone number are collected automatically. No need to include these in your questionnaire. Download the data from your web app'
				rootProps={{
					component: 'form',
					onSubmit: handleFormSubmit,
				}}
				submitButtonProps={{
					type: 'submit',
					loading: questionnaireForm.formState.isSubmitting,
				}}
				contentBoxRef={formRef}
			>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: '16px',
						paddingLeft: '8px',
					}}
				>
					{fields.map((field, index) => (
						<AttendeeRegistrationField
							key={field.key}
							index={index}
							questionnaireModalType={type}
							questionnaireImages={questionnaireImages}
							shouldExpandAdvancedSettings={shouldValidateInitialData}
							tiersOptions={tiersOptions}
							onRemove={() => {
								remove(index);
							}}
							onMove={move}
							canDrag={fields.length > 1}
						/>
					))}
				</Box>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
					}}
				>
					<CoreLinkButton type="button" onClick={addField}>
						<PlusIcon width={16} height={16} />
						Add question
					</CoreLinkButton>

					<Showable show={isPaidEvent}>
						<CoreLabeledCheckbox
							label="Show form once for multiple tickets"
							name="showSectionsOnceForMultipleTickets"
							checked={onceForMultipleTickets}
							onChange={(e, checked) => {
								questionnaireForm.setValue('showSectionsOnceForMultipleTickets', checked);
							}}
						/>
					</Showable>
				</Box>
			</RightBlackModal>
		</FormProvider>
	);
};
